import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Select,
  Input,
  Tag,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import axios from "axios";
import SendLawyerDetailModal from "./modal/SendLawyerDetailModal";
import { mainlawyer_V2 } from "../../API/apiUrls";
import { exportLawyerExcelV2 } from "./export/ExportLawyerExcelV2";
import useLawyerNamesNew from "../../../hook/UseLawyerNameNew";

const { Text } = Typography;

const thb = (n) =>
  Number(n || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (raw) => {
  if (!raw) return "-";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("th-TH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export default function SendLawyerPageV2() {
  const USERNAME = localStorage.getItem("USERNAME");
  const ROLE_ID = localStorage.getItem("ROLE_ID");
  const userCompany = localStorage.getItem("COMPANY_ID");
  const { nameLawyers, loadingUseLawyerNames } = useLawyerNamesNew({
    auto: true,
    toast: false,
  });
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchContno, setSearchContno] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [lawyerName, setLawyerName] = useState(null);
  const [lawyerNameThai, setLawyerNameThai] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const playload = {
      billcoll_lawyer: USERNAME,
      role_id: ROLE_ID,
    };
    try {
      const res = await axios.post(mainlawyer_V2, playload);
      const list = res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
      const visibleList =
        ROLE_ID === "1" || ROLE_ID === "2"
          ? list
          : list.filter((item) => item.lawyer_user_id === USERNAME);
      setData(visibleList);
      setFilteredData(visibleList);
    } catch (err) {
      console.error(err);
      message.error("โหลดข้อมูลไม่สำเร็จ");
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const filterLawyersByCompany = useCallback((lawyers, userCompany) => {
    if (!Array.isArray(lawyers) || !userCompany) return [];

    const companyIds =
      userCompany === "1" || userCompany === "2" ? [1, 2] : [3];

    return lawyers.filter(
      (item) =>
        companyIds.includes(item.COMPANY_ID) &&
        item.ROLE_ID === 3 &&
        item.ACTIVE_STATUS === 1,
    );
  }, []);

  const transformLawyersToOptions = useCallback((lawyers) => {
    return lawyers.map((item) => ({
      value: item.USERNAME,
      label: item.NNAME,
      data: item,
      name: item.USERNAME,
    }));
  }, []);

  const lawyersOption = useMemo(() => {
    if (!nameLawyers?.length || !userCompany) return [];

    const filtered = filterLawyersByCompany(nameLawyers, userCompany);
    const options = transformLawyersToOptions(filtered);

    return [...options];
  }, [
    nameLawyers,
    userCompany,
    filterLawyersByCompany,
    transformLawyersToOptions,
  ]);

  const lawyerNameByUsername = useMemo(() => {
    const map = {};
    (nameLawyers || []).forEach((item) => {
      if (item.USERNAME) map[item.USERNAME] = item.NNAME;
    });
    return map;
  }, [nameLawyers]);

  const getLawyerDisplayName = useCallback(
    (username) => lawyerNameByUsername[username] || username || "-",
    [lawyerNameByUsername],
  );

  const handleFilter = useCallback(() => {
    let filtered = data;

    if (lawyerName) {
      filtered = filtered.filter((d) => d.billcoll_lawyer === lawyerName);
    }

    const query = searchContno.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (d) =>
          d.borr_code?.toLowerCase().includes(query) ||
          d.name?.toLowerCase().includes(query) ||
          d.account_name?.toLowerCase().includes(query),
      );
    }

    setFilteredData(filtered);
    setSelectedRowKeys([]);
  }, [data, statusFilter, lawyerName, searchContno]);

  const handleResetFilters = useCallback(() => {
    setStatusFilter("all");
    setLawyerName(undefined);
    setSearchContno("");
    setFilteredData(data);
    setSelectedRowKeys([]);
  }, [data]);

  const hasActiveFilters =
    statusFilter !== "all" || !!lawyerName || !!searchContno.trim();

  const getStatusLabel = useCallback((r) => {
    if (r.flag_send_lawyer && !r.flag_judgment) return "ส่งฟ้องปกติ";
    if (r.flag_send_lawyer && r.flag_judgment && !r.flag_heir_manager)
      return "ยื่นบุริมสิทธิ";
    if (r.flag_send_lawyer && !r.flag_judgment && r.flag_heir_manager)
      return "ส่งฟ้องผู้จัดการมรดก";
    return "-";
  }, []);

  const handleExportExcel = () => {
    const rows =
      selectedRowKeys.length > 0
        ? filteredData.filter((r) => selectedRowKeys.includes(r.borr_code))
        : filteredData;

    if (rows.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }

    const exportRows = rows.map((r) => ({
      ...r,
      statusLabel: getStatusLabel(r),
      lawyerDisplayName: getLawyerDisplayName(r.billcoll_lawyer),
    }));

    exportLawyerExcelV2(exportRows);
  };

  const stockContractColumns = [
    {
      title: "เลขที่สัญญา",
      dataIndex: "running_no",
      key: "running_no",
      align: "center",
      width: 120,
      render: (v) => v || "-",
    },
    {
      title: "เลขที่บัญชี",
      dataIndex: "account_no",
      key: "account_no",
      align: "center",
      width: 160,
      render: (v) => v || "-",
    },
    {
      title: "เงินต้น",
      dataIndex: "principal_amount",
      key: "principal_amount",
      align: "right",
      width: 140,
      render: (v) => thb(v),
    },
    {
      title: "ดอกเบี้ย SCB",
      dataIndex: "interest",
      key: "interest",
      align: "right",
      width: 140,
      render: (v) => thb(v),
    },
    {
      title: "วันที่ทำสัญญา",
      dataIndex: "sign_date",
      key: "sign_date",
      align: "center",
      width: 120,
      render: fmtDate,
    },
    {
      title: "วันครบสัญญา",
      dataIndex: "end_date",
      key: "end_date",
      align: "center",
      width: 120,
      render: fmtDate,
    },
  ];

  const expandedRowRender = (record) => {
    const contracts = Array.isArray(record.stock_contracts)
      ? record.stock_contracts
      : [];

    if (contracts.length === 0) {
      return (
        <Text type="secondary" style={{ fontSize: 13 }}>
          ไม่มีข้อมูลบัญชีสัญญา
        </Text>
      );
    }

    const totalPrincipal = contracts.reduce(
      (sum, c) => sum + Number(c.principal_amount || 0),
      0,
    );
    const totalInterest = contracts.reduce(
      (sum, c) => sum + Number(c.interest || 0),
      0,
    );

    return (
      <Table
        size="small"
        columns={stockContractColumns}
        dataSource={contracts}
        rowKey="id_sc"
        pagination={false}
        bordered
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2} align="right">
                <Text strong>รวม</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Text strong>{thb(totalPrincipal)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <Text strong>{thb(totalInterest)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} colSpan={2} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    );
  };

  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "ข้อมูลลูกหนี้",
      key: "detail",
      align: "left",
      width: 260,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <Text strong>{r.full_name || r.full_name || "-"}</Text>
          </div>
          <div style={{ color: "#888" }}>รหัสลูกหนี้ {r.borr_code || "-"}</div>
        </div>
      ),
    },
    {
      title: "สถานะคดี",
      key: "status",
      align: "left",
      width: 220,
      render: (_, r) => {
        return (
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <Tag
              color={
                r.flag_send_lawyer && !r.flag_judgment
                  ? "red"
                  : r.flag_send_lawyer &&
                      r.flag_judgment &&
                      !r.flag_heir_manager
                    ? "purple"
                    : r.flag_send_lawyer &&
                        !r.flag_judgment &&
                        r.flag_heir_manager
                      ? "orange"
                      : null
              }
            >
              {getStatusLabel(r) !== "-" ? getStatusLabel(r) : null}
            </Tag>

            <div>
              <Text strong>{getLawyerDisplayName(r.billcoll_lawyer)}</Text>
            </div>
          </div>
        );
      },
    },

    {
      title: "การจัดการ",
      align: "left",
      width: 180,
      render: (_, r) => (
        <Button
          onClick={() => {
            setSelected(r);
            setLawyerNameThai(getLawyerDisplayName(r.billcoll_lawyer));
          }}
        >
          ดูรายละเอียด
        </Button>
      ),
    },
  ];

  return (
    <>
      <style>{`
        .row-even td { background-color: #f5f8ff !important; }
        .row-odd td  { background-color: #ffffff !important; }
      `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>รายงานสัญญาที่ส่งทนาย</Divider>

          {/* ── แถบกรองข้อมูล ── */}
          <Card
            size="small"
            title={
              <Space>
                <FilterOutlined />
                <span>ตัวกรองข้อมูล</span>
              </Space>
            }
            style={{ marginBottom: 12, background: "#fafafa" }}
          >
            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={12} md={6} lg={5}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ค้นหา (รหัสลูกหนี้ / ชื่อ)
                </Text>
                <Input
                  placeholder="เช่น BU 03444"
                  value={searchContno}
                  onChange={(e) => setSearchContno(e.target.value)}
                  onPressEnter={handleFilter}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  allowClear
                  size="small"
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ทนายที่รับผิดชอบ
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={lawyersOption}
                  value={lawyerName}
                  onChange={setLawyerName}
                  popupMatchSelectWidth={false}
                  allowClear
                  showSearch
                  placeholder="ทั้งหมด"
                  size="large"
                  disabled={ROLE_ID > 2}
                  loading={loadingUseLawyerNames}
                />
              </Col>

              <Col flex="auto" style={{ textAlign: "right" }}>
                <Space>
                  <Button
                    color="orange"
                    variant="outlined"
                    icon={<FilterOutlined />}
                    onClick={handleFilter}
                  >
                    กรองข้อมูล
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleResetFilters}
                    color="red"
                    variant="outlined"
                  >
                    ล้างตัวกรอง
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* ── สรุปตัวกรองที่ใช้อยู่ ── */}
          {hasActiveFilters && (
            <Card
              size="small"
              style={{ background: "#f0f5ff", marginBottom: 12 }}
            >
              <Space split="|" size="large" wrap>
                <span>
                  <strong>ผลการค้นหา:</strong> {filteredData.length} รายการ
                </span>
                {searchContno && (
                  <span>
                    <strong>ค้นหา:</strong> "{searchContno}"
                  </span>
                )}
                {lawyerName && (
                  <span>
                    <strong>ทนายที่รับผิดชอบ:</strong>{" "}
                    {getLawyerDisplayName(lawyerName)}
                  </span>
                )}
              </Space>
            </Card>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              strong
              style={{
                margin: "10px 0",
              }}
            >
              จำนวนสัญญาทั้งหมด : {data.length} เคส
              {selectedRowKeys.length > 0 &&
                ` (เลือก ${selectedRowKeys.length} รายการ)`}
            </Text>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              color="green"
              variant="outlined"
            >
              Export Excel
            </Button>
          </div>
          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey="borr_code"
            // onRow={(r) => ({ onClick: () => setSelected(r) })}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            expandable={{
              expandedRowRender,
              rowExpandable: (r) =>
                Array.isArray(r.stock_contracts) &&
                r.stock_contracts.length > 0,
            }}
            scroll={{ x: 1100 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
          />
        </Card>
      </Spin>

      {selected && (
        <SendLawyerDetailModal
          item={selected}
          onClose={() => setSelected(null)}
          lawyer={lawyerNameThai}
        />
      )}
    </>
  );
}
